import { useState, useRef, useEffect } from "react";
import { Send, Bot } from "lucide-react";

const Copilot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your Prescription Copilot. How can I assist you with patient care today?",
      sender: "other",
      timestamp: "10:30 AM",
    },
    {
      id: 2,
      text: "Hi! I need help with dosage recommendations for a hypertensive patient.",
      sender: "me",
      timestamp: "10:32 AM",
    },
    {
      id: 3,
      text: "I'd be happy to help! Could you provide the patient's age, current medications, and blood pressure readings?",
      sender: "other",
      timestamp: "10:35 AM",
    },
    {
      id: 4,
      text: "Patient is 45 years old, currently on Lisinopril 10mg, BP is 150/95",
      sender: "me",
      timestamp: "10:36 AM",
    },
    {
      id: 5,
      text: "Based on the information, consider increasing Lisinopril to 20mg or adding Amlodipine 5mg. Monitor BP in 2 weeks. Always verify with current guidelines! 📋",
      sender: "other",
      timestamp: "10:37 AM",
    },
  ]);

  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 24;
      const maxHeight = lineHeight * 8;
      const minHeight = lineHeight * 1;

      textarea.style.height =
        Math.min(Math.max(scrollHeight, minHeight), maxHeight) + "px";
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  const handleSend = () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        text: newMessage,
        sender: "me",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages([...messages, message]);
      setNewMessage("");

      // Simulate typing indicator and response
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const responses = [
          "I understand your concern. Let me help you with that medical query.",
          "That's a great question! Based on clinical guidelines, I recommend...",
          "For that condition, please consider these treatment options...",
          "I'll provide you with evidence-based recommendations for this case.",
        ];
        const randomResponse =
          responses[Math.floor(Math.random() * responses.length)];

        const response = {
          id: messages.length + 2,
          text: randomResponse,
          sender: "other",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, response]);
      }, 2000);
    }
  };

  return (
    <div className="flex flex-col max-h-96 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden rounded-2xl mb-4 shadow-lg border border-white/50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-4 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">
                Prescription Copilot
              </p>
              <p className="text-xs text-blue-100">AI Medical Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-blue-100">Online</span>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-4 scrollbar-thin bg-gradient-to-b from-transparent to-white/30">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "me" ? "justify-end" : "justify-start"
            } animate-fade-in`}
          >
            <div
              className={`flex max-w-xs lg:max-w-md ${
                message.sender === "me" ? "flex-row-reverse" : "flex-row"
              } items-end space-x-2`}
            >
              <div className="flex flex-col">
                <div
                  className={`px-2 py-2 rounded-2xl shadow-lg transition-all duration-200 hover:shadow-xl backdrop-blur-sm ${
                    message.sender === "me"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-md border border-blue-400/30"
                      : "bg-white/90 text-gray-800 rounded-bl-md border border-gray-200/50 shadow-md"
                  }`}
                >
                  <p className="text-sm leading-relaxed font-medium p-1">
                    {message.text}
                  </p>
                </div>
                <p
                  className={`text-xs text-gray-500 mt-1 font-medium ${
                    message.sender === "me" ? "text-right" : "text-left"
                  }`}
                >
                  {message.timestamp}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex justify-start animate-fade-in">
            <div className="flex items-end space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-md">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-2xl rounded-bl-md shadow-lg border border-gray-200/50">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gradient-to-r from-pink-400 to-red-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-gradient-to-t from-white/80 via-blue-50/80 to-indigo-50/80 backdrop-blur-md border-t border-white/30 px-4 py-4 shadow-xl">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Ask about medications, dosages, interactions..."
            className="w-full pl-4 pr-14 py-3 border-2 border-gray-200/60 rounded-2xl focus:border-indigo-400 focus:outline-none resize-none text-sm bg-white/95 backdrop-blur-sm transition-all duration-200 font-medium placeholder-gray-500 shadow-inner hover:shadow-lg focus:shadow-xl focus:bg-white"
            style={{
              minHeight: "54px",
              lineHeight: "27px",
              maxHeight: "216px",
            }}
            rows="1"
          />

          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className={`absolute right-3 bottom-4 p-2.5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl ${
              newMessage.trim()
                ? "bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-600 text-white hover:scale-105 active:scale-95 shadow-indigo-300/50 hover:shadow-purple-400/50"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Copilot;
